export interface Point2 {
  x: number;
  y: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export type Mat3 = [number, number, number, number, number, number, number, number, number];
export type FourCorners = readonly [Point2, Point2, Point2, Point2];

export type PlanarCalibrationErrorCode =
  | 'invalid-input'
  | 'invalid-corners'
  | 'singular-homography'
  | 'focal-not-observable'
  | 'implausible-focal-length'
  | 'camera-behind-tv';

export class PlanarCalibrationError extends Error {
  readonly code: PlanarCalibrationErrorCode;

  constructor(code: PlanarCalibrationErrorCode, message: string) {
    super(message);
    this.name = 'PlanarCalibrationError';
    this.code = code;
  }
}

export interface PlanarPose {
  focalLengthPx: number;
  principalPointPx: Point2;
  homographyTvToImage: Mat3;
  /** Maps a point in the TV frame to the CV camera frame (+x right, +y down, +z forward). */
  rotationTvToCamera: Mat3;
  translationTvOriginInCamera: Vec3;
  cameraPositionInTv: Vec3;
  rotationCameraToTv: Mat3;
  reprojectionRmsPx: number;
  viewObliquityDeg: number;
}

const EPS = 1e-12;
const DEG = 180 / Math.PI;

const finite = (...values: number[]): boolean => values.every(Number.isFinite);
const add3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const scale3 = (v: Vec3, scale: number): Vec3 => ({ x: v.x * scale, y: v.y * scale, z: v.z * scale });
const dot3 = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const norm3 = (v: Vec3): number => Math.hypot(v.x, v.y, v.z);
const normalize3 = (v: Vec3): Vec3 => {
  const length = norm3(v);
  if (length < EPS) throw new PlanarCalibrationError('singular-homography', 'Calibration axis collapsed');
  return scale3(v, 1 / length);
};
const cross3 = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

function mat3Multiply(a: readonly number[], b: readonly number[]): Mat3 {
  const out = new Array<number>(9).fill(0);
  for (let row = 0; row < 3; row += 1) {
    for (let col = 0; col < 3; col += 1) {
      for (let k = 0; k < 3; k += 1) out[row * 3 + col]! += a[row * 3 + k]! * b[k * 3 + col]!;
    }
  }
  return out as Mat3;
}

function mat3Transpose(matrix: Mat3): Mat3 {
  return [matrix[0], matrix[3], matrix[6], matrix[1], matrix[4], matrix[7], matrix[2], matrix[5], matrix[8]];
}

function mat3Vector(matrix: Mat3, vector: Vec3): Vec3 {
  return {
    x: matrix[0] * vector.x + matrix[1] * vector.y + matrix[2] * vector.z,
    y: matrix[3] * vector.x + matrix[4] * vector.y + matrix[5] * vector.z,
    z: matrix[6] * vector.x + matrix[7] * vector.y + matrix[8] * vector.z,
  };
}

function mat3Inverse(matrix: Mat3): Mat3 {
  const [a, b, c, d, e, f, g, h, i] = matrix;
  const A = e * i - f * h;
  const B = f * g - d * i;
  const C = d * h - e * g;
  const determinant = a * A + b * B + c * C;
  if (Math.abs(determinant) < EPS) throw new PlanarCalibrationError('singular-homography', 'Homography is singular');
  return [
    A / determinant,
    (c * h - b * i) / determinant,
    (b * f - c * e) / determinant,
    B / determinant,
    (a * i - c * g) / determinant,
    (c * d - a * f) / determinant,
    C / determinant,
    (b * g - a * h) / determinant,
    (a * e - b * d) / determinant,
  ];
}

/** Scaled partial-pivot Gaussian elimination; matrices here are at most 8×8. */
function solveLinear(inputMatrix: readonly (readonly number[])[], inputVector: readonly number[]): number[] {
  const n = inputVector.length;
  const matrix = inputMatrix.map((row, index) => [...row, inputVector[index]!]);
  const scales = matrix.map((row) => Math.max(...row.slice(0, n).map(Math.abs)));
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    let score = -1;
    for (let row = col; row < n; row += 1) {
      const candidate = scales[row]! > 0 ? Math.abs(matrix[row]![col]!) / scales[row]! : 0;
      if (candidate > score) {
        pivot = row;
        score = candidate;
      }
    }
    if (score < 1e-13) throw new PlanarCalibrationError('singular-homography', 'Calibration equations are singular');
    [matrix[col], matrix[pivot]] = [matrix[pivot]!, matrix[col]!];
    [scales[col], scales[pivot]] = [scales[pivot]!, scales[col]!];
    const divisor = matrix[col]![col]!;
    for (let k = col; k <= n; k += 1) matrix[col]![k] = matrix[col]![k]! / divisor;
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = matrix[row]![col]!;
      if (factor === 0) continue;
      for (let k = col; k <= n; k += 1) matrix[row]![k] = matrix[row]![k]! - factor * matrix[col]![k]!;
    }
  }
  return matrix.map((row) => row[n]!);
}

function normalizePoints(points: readonly Point2[]): { points: Point2[]; transform: Mat3 } {
  const center = points.reduce((sum, point) => ({ x: sum.x + point.x, y: sum.y + point.y }), { x: 0, y: 0 });
  center.x /= points.length;
  center.y /= points.length;
  const rms = Math.sqrt(points.reduce((sum, point) => sum + (point.x - center.x) ** 2 + (point.y - center.y) ** 2, 0) / points.length);
  if (rms < EPS) throw new PlanarCalibrationError('invalid-corners', 'Calibration corners collapse to one point');
  const scale = Math.SQRT2 / rms;
  return {
    points: points.map((point) => ({ x: scale * (point.x - center.x), y: scale * (point.y - center.y) })),
    transform: [scale, 0, -scale * center.x, 0, scale, -scale * center.y, 0, 0, 1],
  };
}

function polygonArea(points: FourCorners): number {
  let twiceArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    twiceArea += current.x * next.y - current.y * next.x;
  }
  return twiceArea / 2;
}

function validateCorners(corners: FourCorners, imageWidthPx: number, imageHeightPx: number): void {
  if (!finite(imageWidthPx, imageHeightPx) || imageWidthPx <= 0 || imageHeightPx <= 0) {
    throw new PlanarCalibrationError('invalid-input', 'Image dimensions must be positive');
  }
  for (const point of corners) {
    if (!finite(point.x, point.y)) throw new PlanarCalibrationError('invalid-corners', 'Every corner must be finite');
    if (point.x < 0 || point.x > imageWidthPx || point.y < 0 || point.y > imageHeightPx) {
      throw new PlanarCalibrationError('invalid-corners', 'A corner is outside the decoded image');
    }
  }
  const turns: number[] = [];
  for (let index = 0; index < 4; index += 1) {
    const a = corners[index]!;
    const b = corners[(index + 1) % 4]!;
    const c = corners[(index + 2) % 4]!;
    turns.push((b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x));
  }
  if (turns.some((turn) => Math.abs(turn) < 1e-6) || !(turns.every((turn) => turn > 0) || turns.every((turn) => turn < 0))) {
    throw new PlanarCalibrationError('invalid-corners', 'Tap the four corners in TL, TR, BR, BL order');
  }
  if (Math.abs(polygonArea(corners)) < imageWidthPx * imageHeightPx * 0.002) {
    throw new PlanarCalibrationError('invalid-corners', 'The TV occupies too little of the image');
  }
}

export function solveHomography(source: FourCorners, destination: FourCorners): Mat3 {
  const src = normalizePoints(source);
  const dst = normalizePoints(destination);
  const matrix: number[][] = [];
  const vector: number[] = [];
  for (let index = 0; index < 4; index += 1) {
    const { x, y } = src.points[index]!;
    const { x: u, y: v } = dst.points[index]!;
    matrix.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    vector.push(u);
    matrix.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    vector.push(v);
  }
  const q = solveLinear(matrix, vector);
  const normalized: Mat3 = [q[0]!, q[1]!, q[2]!, q[3]!, q[4]!, q[5]!, q[6]!, q[7]!, 1];
  const denormalized = mat3Multiply(mat3Multiply(mat3Inverse(dst.transform), normalized), src.transform);
  const scale = Math.max(...denormalized.map(Math.abs));
  if (!Number.isFinite(scale) || scale < EPS) throw new PlanarCalibrationError('singular-homography', 'Homography has no scale');
  return denormalized.map((value) => value / scale) as Mat3;
}

function columnsToMatrix(first: Vec3, second: Vec3, third: Vec3): Mat3 {
  return [first.x, second.x, third.x, first.y, second.y, third.y, first.z, second.z, third.z];
}

function rotationColumns(rotation: Mat3): [Vec3, Vec3, Vec3] {
  return [
    { x: rotation[0], y: rotation[3], z: rotation[6] },
    { x: rotation[1], y: rotation[4], z: rotation[7] },
    { x: rotation[2], y: rotation[5], z: rotation[8] },
  ];
}

function expRotation(vector: Vec3): Mat3 {
  const angle = norm3(vector);
  if (angle < 1e-12) {
    return [1, -vector.z, vector.y, vector.z, 1, -vector.x, -vector.y, vector.x, 1];
  }
  const axis = scale3(vector, 1 / angle);
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const t = 1 - c;
  return [
    t * axis.x * axis.x + c,
    t * axis.x * axis.y - s * axis.z,
    t * axis.x * axis.z + s * axis.y,
    t * axis.x * axis.y + s * axis.z,
    t * axis.y * axis.y + c,
    t * axis.y * axis.z - s * axis.x,
    t * axis.x * axis.z - s * axis.y,
    t * axis.y * axis.z + s * axis.x,
    t * axis.z * axis.z + c,
  ];
}

function project(point: Vec3, rotation: Mat3, translation: Vec3, focal: number, principal: Point2): Point2 & { depth: number; camera: Vec3 } {
  const camera = add3(mat3Vector(rotation, point), translation);
  return {
    x: principal.x + (focal * camera.x) / camera.z,
    y: principal.y + (focal * camera.y) / camera.z,
    depth: camera.z,
    camera,
  };
}

function residualState(
  points: readonly Vec3[],
  observed: FourCorners,
  rotation: Mat3,
  translation: Vec3,
  focal: number,
  principal: Point2,
): { residuals: number[]; cost: number; depthsPositive: boolean; jacobian?: number[][] } {
  const residuals: number[] = [];
  const jacobian: number[][] = [];
  let depthsPositive = true;
  for (let index = 0; index < points.length; index += 1) {
    const modelPoint = points[index]!;
    const rendered = project(modelPoint, rotation, translation, focal, principal);
    depthsPositive &&= rendered.depth > 1e-5;
    residuals.push(rendered.x - observed[index]!.x, rendered.y - observed[index]!.y);

    const a = mat3Vector(rotation, modelPoint);
    const { x, y, z } = rendered.camera;
    const projectionRows = [
      [focal / z, 0, (-focal * x) / (z * z)],
      [0, focal / z, (-focal * y) / (z * z)],
    ];
    const minusSkew = [
      [0, a.z, -a.y],
      [-a.z, 0, a.x],
      [a.y, -a.x, 0],
    ];
    for (let output = 0; output < 2; output += 1) {
      const row = projectionRows[output]!;
      const rotationPart = [0, 1, 2].map((column) => row.reduce((sum, value, k) => sum + value * minusSkew[k]![column]!, 0));
      jacobian.push([
        ...rotationPart,
        ...row,
        output === 0 ? rendered.x - principal.x : rendered.y - principal.y,
      ]);
    }
  }
  return {
    residuals,
    cost: residuals.reduce((sum, value) => sum + value * value, 0),
    depthsPositive,
    jacobian,
  };
}

function refinePose(
  modelPoints: readonly Vec3[],
  observed: FourCorners,
  principal: Point2,
  initialRotation: Mat3,
  initialTranslation: Vec3,
  initialFocal: number,
): { rotation: Mat3; translation: Vec3; focal: number; cost: number } {
  let rotation = initialRotation;
  let translation = initialTranslation;
  let focal = initialFocal;
  let damping = 1e-3;
  let state = residualState(modelPoints, observed, rotation, translation, focal, principal);
  for (let iteration = 0; iteration < 35; iteration += 1) {
    const normal = Array.from({ length: 7 }, () => new Array<number>(7).fill(0));
    const gradient = new Array<number>(7).fill(0);
    for (let row = 0; row < state.residuals.length; row += 1) {
      const j = state.jacobian![row]!;
      const residual = state.residuals[row]!;
      for (let a = 0; a < 7; a += 1) {
        gradient[a]! += j[a]! * residual;
        for (let b = 0; b < 7; b += 1) normal[a]![b]! += j[a]! * j[b]!;
      }
    }
    for (let diagonal = 0; diagonal < 7; diagonal += 1) {
      normal[diagonal]![diagonal]! += damping * Math.max(normal[diagonal]![diagonal]!, 1);
    }
    let delta: number[];
    try {
      delta = solveLinear(normal, gradient.map((value) => -value));
    } catch {
      break;
    }
    if (Math.hypot(...delta) < 1e-10) break;
    const candidateRotation = mat3Multiply(expRotation({ x: delta[0]!, y: delta[1]!, z: delta[2]! }), rotation);
    const candidateTranslation = {
      x: translation.x + delta[3]!,
      y: translation.y + delta[4]!,
      z: translation.z + delta[5]!,
    };
    const candidateFocal = focal * Math.exp(delta[6]!);
    if (!Number.isFinite(candidateFocal) || candidateFocal <= 0) {
      damping *= 8;
      continue;
    }
    const candidate = residualState(modelPoints, observed, candidateRotation, candidateTranslation, candidateFocal, principal);
    if (candidate.depthsPositive && candidate.cost < state.cost) {
      rotation = candidateRotation;
      translation = candidateTranslation;
      focal = candidateFocal;
      state = candidate;
      damping = Math.max(1e-9, damping / 3);
    } else {
      damping = Math.min(1e9, damping * 8);
    }
  }
  return { rotation, translation, focal, cost: state.cost };
}

function cameraPosition(rotation: Mat3, translation: Vec3): Vec3 {
  return scale3(mat3Vector(mat3Transpose(rotation), translation), -1);
}

/**
 * Recover a pinhole camera from the visible TV rectangle.
 * Corners must be decoded-image pixels in TL, TR, BR, BL order (never CSS pixels).
 */
export function solvePlanarTvPose(
  corners: FourCorners,
  tvWidthM: number,
  tvHeightM: number,
  imageWidthPx: number,
  imageHeightPx: number,
): PlanarPose {
  if (!finite(tvWidthM, tvHeightM) || tvWidthM <= 0 || tvHeightM <= 0) {
    throw new PlanarCalibrationError('invalid-input', 'TV active-picture dimensions must be positive');
  }
  validateCorners(corners, imageWidthPx, imageHeightPx);
  const source: FourCorners = [
    { x: -tvWidthM / 2, y: tvHeightM / 2 },
    { x: tvWidthM / 2, y: tvHeightM / 2 },
    { x: tvWidthM / 2, y: -tvHeightM / 2 },
    { x: -tvWidthM / 2, y: -tvHeightM / 2 },
  ];
  const homography = solveHomography(source, corners);
  const principal = { x: imageWidthPx / 2, y: imageHeightPx / 2 };
  const [h11, h12, h13, h21, h22, h23, h31, h32, h33] = homography;
  const x1 = h11 - principal.x * h31;
  const y1 = h21 - principal.y * h31;
  const z1 = h31;
  const x2 = h12 - principal.x * h32;
  const y2 = h22 - principal.y * h32;
  const z2 = h32;
  const A1 = x1 * x2 + y1 * y2;
  const B1 = z1 * z2;
  const A2 = x1 * x1 + y1 * y1 - x2 * x2 - y2 * y2;
  const B2 = z1 * z1 - z2 * z2;
  const denominator = A1 * A1 + 0.25 * A2 * A2;
  if (denominator < 1e-18 * Math.max(1, ...homography.map((value) => value * value))) {
    throw new PlanarCalibrationError('focal-not-observable', 'Move the camera sideways: a front-on TV cannot reveal focal length');
  }
  const beta = -(A1 * B1 + 0.25 * A2 * B2) / denominator;
  if (!Number.isFinite(beta) || beta <= 0) {
    throw new PlanarCalibrationError('focal-not-observable', 'This view cannot determine a physical focal length');
  }
  let focal = Math.sqrt(1 / beta);
  const imageScale = Math.max(imageWidthPx, imageHeightPx);
  if (focal < imageScale * 0.15 || focal > imageScale * 20) {
    throw new PlanarCalibrationError('implausible-focal-length', 'Recovered focal length is outside a plausible camera range');
  }

  const inverseKColumn = (x: number, y: number, z: number): Vec3 => ({ x: x / focal, y: y / focal, z });
  const sign = h33 >= 0 ? 1 : -1;
  const b1 = scale3(inverseKColumn(x1, y1, z1), sign);
  const b2 = scale3(inverseKColumn(x2, y2, z2), sign);
  const b3 = scale3(inverseKColumn(h13 - principal.x * h33, h23 - principal.y * h33, h33), sign);
  const u = normalize3(b1);
  const v = normalize3(b2);
  const uv = Math.max(-0.999999999, Math.min(0.999999999, dot3(u, v)));
  const plus = scale3(add3(u, v), 1 / Math.sqrt(2 * (1 + uv)));
  const minus = scale3(add3(u, scale3(v, -1)), 1 / Math.sqrt(2 * (1 - uv)));
  const r1 = normalize3(scale3(add3(plus, minus), Math.SQRT1_2));
  const r2 = normalize3(scale3(add3(plus, scale3(minus, -1)), Math.SQRT1_2));
  const r3 = normalize3(cross3(r1, r2));
  let rotation = columnsToMatrix(r1, r2, r3);
  const metricScale = (dot3(b1, r1) + dot3(b2, r2)) / (dot3(b1, b1) + dot3(b2, b2));
  let translation = scale3(b3, metricScale);

  const modelPoints = source.map((point) => ({ x: point.x, y: point.y, z: 0 }));
  const initialObliquity = Math.asin(Math.min(1, Math.hypot(r1.z, r2.z))) * DEG;
  if (!Number.isFinite(initialObliquity) || initialObliquity < 8) {
    throw new PlanarCalibrationError('focal-not-observable', 'Move at least 8° off-centre before freezing the frame');
  }

  const refined = refinePose(modelPoints, corners, principal, rotation, translation, focal);
  rotation = refined.rotation;
  translation = refined.translation;
  focal = refined.focal;
  const [refinedR1, refinedR2] = rotationColumns(rotation);
  const viewObliquityDeg = Math.asin(Math.min(1, Math.hypot(refinedR1.z, refinedR2.z))) * DEG;
  const position = cameraPosition(rotation, translation);
  const finalState = residualState(modelPoints, corners, rotation, translation, focal, principal);
  if (!finalState.depthsPositive || position.z <= 0) {
    throw new PlanarCalibrationError('camera-behind-tv', 'Recovered camera is behind the visible side of the TV');
  }

  return {
    focalLengthPx: focal,
    principalPointPx: principal,
    homographyTvToImage: homography,
    rotationTvToCamera: rotation,
    translationTvOriginInCamera: translation,
    cameraPositionInTv: position,
    rotationCameraToTv: mat3Transpose(rotation),
    reprojectionRmsPx: Math.sqrt(finalState.cost / 8),
    viewObliquityDeg,
  };
}
